<template>
  <fragment :key="version">
    <header :wxid="$options.name">
      <h1 class="main-title mb-sm-4 main-title--toggle-buttons-active">
        <span class="main-title__text">
          {{ productionUnitNameAsTitle }}
        </span>
        <v-btn-toggle class="main-title__buttons ml-sm-3 ml-md-6" borderless dense>
          <wx-btn-standard
            @click="toProduction()"
            :title="$t('dashboard.timelineToggleBtn_hint')"
            :color="getTimelineButtonColor()"
            :class="{ disabled: !giveawayViewToggle }"
            class="production-toggle"
          >
            {{ $t("dashboard.timelineToggleBtn") }}
          </wx-btn-standard>
          <wx-btn-standard
            @click="toGiveaway()"
            v-if="isGiveawayActive"
            :title="$t('dashboard.giveawayToggleBtn_hint')"
            :color="getGiveawayButtonColor()"
            :class="{ disabled: giveawayViewToggle }"
            class="giveaway-toggle"
          >
            {{ $t("dashboard.giveawayToggleBtn") }}
          </wx-btn-standard>
        </v-btn-toggle>
      </h1>
      <v-row class="align-center">
        <v-col cols="6" md="auto">
          <span style="word-break: break-word">
            <strong>{{ productSkuAndNameText }}</strong>
          </span>
        </v-col>
        <v-col cols="6" md="auto">
          <span>
            {{ workOrderAndLotInformationText }}
          </span>
        </v-col>
        <v-col cols="12" md="auto">
          <span v-if="isLiveData" :title="$t('dashboard.liveDataTitle')" class="primary--text font-weight-bold">
            {{ $t("common.live") }}
          </span>
        </v-col>
        <v-col cols="12" md="auto">
          <span v-if="showDashboardRange">
            <span class="d-inline-block">
              <b>{{ $t("common.from") + " " }}</b>
              {{ displayProductionRunOrShiftStartDate() }}
            </span>
            <span class="d-inline-block ml-3">
              <b>{{ $t("common.to") + " " }}</b>
              {{ displayProductionRunOrShiftEndDate() }}
            </span>
          </span>
        </v-col>
        <v-col cols="12" md="auto" class="ml-auto" v-if="!isPresenter">
          <v-badge class="animate-ping" content="!" color="error" overlap v-model="hasActiveEndOfWorkShiftReportAlerts">
            <v-btn id="wx-shift-report-btn" color="primary" @click="openShiftReportDialog">
              {{ $t("shiftReport.form.title") }}
              <v-progress-circular
                v-if="isShiftReportLoading"
                indeterminate
                size="20"
                width="2"
                color="white"
                class="ml-1"
              />
            </v-btn>
          </v-badge>
        </v-col>
      </v-row>
      <wx-time-frame-date-picker
        ref="timeFrameDatePicker"
        :max-past-months="maxPastMonths"
        :work-shift-coverage="workShiftCoverage"
        :production-run-coverage="productionRunCoverage"
        :is-display-optional-column="giveawayViewToggle"
        :row-vertical-spacing-class="giveawayViewToggle ? 'main-title-tab--giveaway' : 'main-title-tab--production'"
        optional-col-class="flex-column flex-sm-row align-center justify-md-end flex-button-column-gap"
      >
        <!-- Must be on the right toggled view and must have a sampling configuration to see this button -->
        <template
          v-if="giveawayViewToggle && isGiveawaySamplingModeActive && !isPresenter"
          v-slot:optional-column-container
        >
          <measures-export-dialog measure-type="giveawaysamples" activator-button-css-class="mb-2 mb-sm-0" />

          <v-btn-toggle
            v-model="samplingButton"
            :disabled="!currentProductId"
            class="d-flex justify-center align-center"
            color="primary"
            multiple
            dense
          >
            <wx-btn-standard
              @click="startSampling(samplingTypeDefault)"
              :title="$t('dashboard.productSampling.startSampling')"
              color="primary"
              class="sampling-btn"
              large
            >
              <v-icon left>mdi-plus</v-icon>
              {{ samplingButtonLabel }}
            </wx-btn-standard>
            <v-menu offset-y nudge-bottom="8">
              <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                <wx-btn-standard color="primary" v-bind="menuAttrs" v-on="menuOn" large>
                  <v-icon>mdi-chevron-down</v-icon>
                </wx-btn-standard>
              </template>
              <v-list>
                <v-list-item @click="startSampling('manual')">
                  <v-list-item-content>
                    <span class="wx-typo-sm">{{ $t("dashboard.productSampling.manualSampling") }}</span>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="startSampling('assisted')">
                  <v-list-item-content>
                    <span class="wx-typo-sm">{{ $t("dashboard.productSampling.assistedSampling") }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn-toggle>
        </template>
        <template v-slot:tag-selector>
          <dashboard-tag-selector />
        </template>
      </wx-time-frame-date-picker>
    </header>
    <article :wxid="$options.name" class="main-content-wrapper">
      <!-- - - - - - - - - -
      Production (default)
      -->
      <section v-if="!giveawayViewToggle" class="dashboard-content dashboard-content--production">
        <v-alert v-if="includesUnknownTiles" class="mt-5 pb-0" color="warning" icon="mdi-alert-circle" outlined text>
          <p>{{ $t("tiles.unknownTileMessage") }}</p>
        </v-alert>

        <v-tabs-items :value="0">
          <v-tab-item :class="{ presenter: isPresenter }">
            <div class="tab-inner">
              <div class="tile-container">
                <section ref="tilesRow" class="tile-slider" :class="{ isDataSourceAlerts: isDataSourceAlerts }">
                  <tile-disconnected v-if="!isMobile" />
                  <component
                    v-for="(tileName, index) in filteredTileSelection"
                    :key="`tileSelection-${index}`"
                    :is="!isTilesLoaded ? 'tile-loading' : 'tile-' + tileName"
                    :tile-index="index"
                    type="image"
                  />
                  <tile-disconnected v-if="isMobile && isDataSourceAlerts" />
                </section>
              </div>

              <div :class="{ isUp: selectedDowntime }" class="panel-container panel-container--production-timeline">
                <jumping-fish />
                <panel-production-timeline v-show="isPanelLoaded" :show-timeline="!isDurationOverThreeDays" />
                <wx-skeleton-loader v-show="!isPanelLoaded" type="image" />
              </div>
              <div
                v-if="isPanelLoaded"
                :class="{ 'hide-panel': !selectedDowntime }"
                class="panel-container panel-container--justification"
              >
                <justification-panel v-show="!isPresenter && selectedDowntime" :panel-height="tilesRowHeight" />
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </section>
      <!-- - - - - - - - - -
      Giveaways
      -->
      <section v-if="giveawayViewToggle" class="dashboard-content dashboard-content--giveaway">
        <v-tabs-items :value="0">
          <v-tab-item>
            <div class="tab-inner">
              <div class="tile-container">
                <section class="tile-slider" :class="{ isDataSourceAlerts: isDataSourceAlerts }">
                  <tile-disconnected v-if="!isMobile" />
                  <component
                    v-for="(tile, index) in giveawayTiles"
                    :key="`giveawayTile-${index}`"
                    :is="!isTilesLoaded ? 'tile-loading' : 'tile-' + tile.name"
                    :tile-index="index"
                  />
                  <tile-disconnected v-if="isMobile && isDataSourceAlerts" />
                </section>
              </div>

              <div class="panel-container panel-container--giveaway">
                <panel-giveaway v-if="isPanelLoaded" :show-giveaway="!isDurationOverThreeDays" />
                <wx-skeleton-loader v-else type="image" />
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </section>
    </article>
    <existing-session-dialog
      :show="showExistingSessionDialog"
      @back="onExistingSessionBack"
      @resume="onExistingSessionResume"
    />
    <wrong-product-selected-dialog
      v-if="samplingSessionInProgress"
      :show="showWrongProductForSessionDialog"
      :session="samplingSessionInProgress"
      @back="onExistingSessionBack"
      @cancel="onWrongProductSelectedCancel"
    />
    <confirm-cancel-dialog :show="cancellingSession" @back="onExistingSessionBack" @cancel="cancelSessionInProgress" />
    <invalid-sampling-session-dialog
      :show="showInvalidSamplingSessionDlg"
      :product-name="productNameAndSku"
      @cancel="hideInvalidSamplingSessionDlg"
      @setupProduct="navigateToInvalidPrd"
    />
    <tile-selector />
    <graph-selector />
    <shift-reports v-model="showShiftDialog" />
  </fragment>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import PanelProductionTimeline from "@/components/dashboard/PanelProductionTimeline";
import PanelGiveaway from "@/components/dashboard/PanelGiveaway";
import JustificationPanel from "@/components/dashboard/JustificationPanel";
import TileAvailability from "@/components/dashboard/tiles/TileAvailability";
import TileTotalUptime from "@/components/dashboard/tiles/TileTotalUptime";
import TileTotalDowntime from "@/components/dashboard/tiles/TileTotalDowntime";
import TileDisconnected from "@/components/dashboard/tiles/TileDisconnected";
import TilePerformance from "@/components/dashboard/tiles/TilePerformance";
import TileOee from "@/components/dashboard/tiles/TileOee";
import TileAllProductsQuantity from "@/components/dashboard/tiles/TileAllProductsQuantity";
import TileCurrentProductQuantity from "@/components/dashboard/tiles/TileCurrentProductQuantity";
import TileProductThroughput from "@/components/dashboard/tiles/TileProductThroughput";
import TileProductThroughputPerMinute from "@/components/dashboard/tiles/TileProductThroughputPerMinute";
import TileProductThroughputPerSecond from "@/components/dashboard/tiles/TileProductThroughputPerSecond";
import TileTotalWeight from "@/components/dashboard/tiles/TileTotalWeight";
import TileTotalVolume from "@/components/dashboard/tiles/TileTotalVolume";
import TileProductSpeed5m from "@/components/dashboard/tiles/TileProductSpeed5m";
import TileProductSpeed5mPerMinute from "@/components/dashboard/tiles/TileProductSpeed5mPerMinute";
import TileProductSpeed5mPerSecond from "@/components/dashboard/tiles/TileProductSpeed5mPerSecond";
import TileTotalLength from "@/components/dashboard/tiles/TileTotalLength";
import TileProductGiveaway from "@/components/dashboard/tiles/TileProductGiveaway";
import TileProductGiveawayPercent from "@/components/dashboard/tiles/TileProductGiveawayPercent";
import TileInProgressDowntime from "@/components/dashboard/tiles/TileInProgressDowntime";
import TileProductAverageGiveaway from "@/components/dashboard/tiles/TileProductAverageGiveaway";
import TileWorkOrder from "@/components/dashboard/tiles/TileWorkOrder";
import TileLot from "@/components/dashboard/tiles/TileLot";
import TileUnknown from "@/components/dashboard/tiles/TileUnknown";
import WxSelect from "@/components/ui/WxSelect";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import PackageFeatures from "@/components/PackageFeatures";
import Tiles, { tileKeyByName } from "@/components/Tiles";
import WxBtnStandard from "@/components/ui/WxBtnStandard";
import RouteService from "@/router/RouteService";
import GiveawaySamplingService from "@/components/productsamplingsession/GiveawaySamplingService";
import ExistingSessionDialog from "@/components/productsamplingsession/dialogs/ExistingSessionDialog";
import WrongProductSelectedDialog from "@/components/productsamplingsession/dialogs/WrongProductSelectedDialog";
import ConfirmCancelDialog from "@/components/productsamplingsession/dialogs/ConfirmCancelDialog";
import TileProduction from "@/components/dashboard/tiles/TileProduction";
import TileQuality from "@/components/dashboard/tiles/TileQuality";
import TileRejectQuantity from "@/components/dashboard/tiles/TileRejectQuantity";
import TileCycleTime from "@/components/dashboard/tiles/TileCycleTime";
import TileCycleQuantity from "@/components/dashboard/tiles/TileCycleQuantity";
import goTo from "vuetify/lib/services/goto";
import DemoService from "@/components/DemoService";
import MeasuresExportDialog from "@/components/productionunit/MeasuresExportDialog";
import WxTimeFrameDatePicker from "@/components/ui/timeframedatepicker/WxTimeFrameDatePicker";
import { isDurationGreaterThanNDays, PRODUCTION_RUN, SHIFT } from "@/store/TimeUtils";
import TileLoading from "@/components/dashboard/tiles/TileLoading";
import WxSkeletonLoader from "@/components/ui/WxSkeletonLoader";
import TileOoe from "@/components/dashboard/tiles/TileOoe";
import DashboardTagSelector from "@/components/ui/tagselector/DashboardTagSelector";
import TileSelector from "@/components/dashboard/tileselectormenu/TileSelector";
import TileTimeToCompletion from "@/components/dashboard/tiles/TileTimeToCompletion";
import GraphSelector from "@/components/dashboard/tileselectormenu/GraphSelector.vue";
import InvalidSamplingSessionDialog from "@/components/productsamplingsession/InvalidSamplingSessionDialog.vue";
import ShiftReports from "@/components/dashboard/shiftReports/ShiftReports.vue";
import {
  SHIFT_REPORT_LOADING_STATE_FETCHING,
  SHIFT_REPORT_LOADING_STATE_IN_PROGRESS,
  SHIFT_REPORT_LOADING_STATE_AWAITING_PARAMS,
} from "@/store/modules/shiftReports";
import JumpingFish from "@/components/animations/JumpingFish.vue";

const INTERVAL_1_SECOND = 1000;
const INTERVAL_10_SECONDS = 10000;
const INTERVAL_1_MINUTE = 60000;

export default {
  name: "DashboardView",
  props: {
    selectedTab: {
      type: String,
      required: false,
      default: "timeline",
    },
  },
  components: {
    JumpingFish,
    ShiftReports,
    TileSelector,
    GraphSelector,
    DashboardTagSelector,
    WxSkeletonLoader,
    TileLoading,
    WxTimeFrameDatePicker,
    ConfirmCancelDialog,
    WrongProductSelectedDialog,
    ExistingSessionDialog,
    InlineSvg,
    PanelProductionTimeline,
    JustificationPanel,
    PanelGiveaway,
    TileAvailability,
    TileTotalUptime,
    TileTotalDowntime,
    TileAllProductsQuantity,
    TileCurrentProductQuantity,
    TileTotalWeight,
    TileTotalVolume,
    TileTotalLength,
    TileProductThroughput,
    TileProductThroughputPerMinute,
    TileProductThroughputPerSecond,
    TileProductSpeed5m,
    TileProductSpeed5mPerMinute,
    TileProductSpeed5mPerSecond,
    TilePerformance,
    TileOee,
    TileProductGiveaway,
    TileProductGiveawayPercent,
    TileInProgressDowntime,
    TileDisconnected,
    TileQuality,
    TileRejectQuantity,
    WxSelect,
    TileProductAverageGiveaway,
    TileWorkOrder,
    TileLot,
    TileProduction,
    TileCycleTime,
    TileCycleQuantity,
    WxBtnStandard,
    MeasuresExportDialog,
    TileOoe,
    TileTimeToCompletion,
    TileUnknown,
    InvalidSamplingSessionDialog,
  },
  data() {
    return {
      version: 1,
      fetchDashboardInterval: null,
      fetchGiveawayDashboardInterval: null,
      setCurrentDowntimeDurationInterval: null,
      fetchDataSourceAlertsInterval: null,
      isDataSourceAlerts: false,
      showExistingSessionDialog: false,
      showWrongProductForSessionDialog: false,
      cancellingSession: false,
      tilesRowHeight: "0px",
      fetchProductionUnitTagsInterval: null,
      samplingButton: [],
      showInvalidSamplingSessionDlg: false,
    };
  },
  computed: {
    ...mapGetters("user", [
      "isPresenter",
      "language",
      "isAccessible",
      "isWorximityAdmin",
      "worximityAdminAllowedCoverageInMonths",
      "email",
      "factories",
      "preferences",
    ]),
    ...mapGetters("dashboard", [
      "giveawayViewToggle",
      "activeProductionUnitId",
      "selectedDowntime",
      "productionUnitName",
      "tileSelection",
      "activeProductionUnit",
      "currentProductionRunCoverage",
      "lastProductionRunsCoverageQueryTimeMillis",
      "currentProductSku",
      "currentProductName",
      "currentWorkOrder",
      "currentLot",
      "currentProductId",
      "samplingSessionInProgress",
      "oldestDataSourceAlert",
      "workShiftCoverage",
      "productionRunCoverage",
      "currentWorkShiftCoverage",
      "productionUnitProducts",
      "isTilesLoaded",
      "isPanelLoaded",
    ]),
    ...mapGetters("shiftReports", ["shiftReports", "isShiftReportsDialogOpen", "shiftReportLoadingState"]),
    ...mapGetters("navigation", ["activeFactory", "isLiveData", "timeFrame", "isTimeRangeReady", "durationMillis"]),
    ...mapGetters("packages", ["activePuHasRequiredFeature"]),
    ...mapGetters("alerts", ["activeAlertInstances", "alerts", "activeAlertInstance"]),
    includesUnknownTiles() {
      return this.filteredTileSelection.includes(Tiles.unknown.name);
    },
    currentProduct() {
      if (!this.currentProductId) {
        return null;
      }
      return this.productionUnitProducts.find((p) => p.id === this.currentProductId);
    },
    productNameAndSku() {
      return `${this.currentProductSku} (${this.currentProductName})`;
    },
    showShiftDialog: {
      get() {
        return this.isShiftReportsDialogOpen;
      },
      set(value) {
        this.setIsShiftReportsDialogOpen(!value);
      },
    },
    filteredTileSelection() {
      // if the tile name is not defined in the UI show unknown tile
      return this.tileSelection.map((name) => (tileKeyByName[name] ? name : Tiles.unknown.name));
    },
    maxPastMonths() {
      if (this.isWorximityAdmin) {
        return this.worximityAdminAllowedCoverageInMonths;
      }
      return 2;
    },
    giveawayTiles() {
      return Tiles.getGiveawayTiles();
    },
    language() {
      return this.$i18n.locale;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    isGiveawayActive() {
      if (!this.activeProductionUnit) return false;
      return this.activePuHasRequiredFeature(PackageFeatures.giveaway);
    },
    isGiveawaySamplingModeActive() {
      if (!this.activeProductionUnit) return false;
      return (
        this.activePuHasRequiredFeature(PackageFeatures.giveaway) &&
        this.activeProductionUnit.giveaway_sampling_configuration !== null
      );
    },
    productionUnitNameAsTitle() {
      if (this.productionUnitName) {
        if (this.productionUnitName.length > 30) {
          return this.productionUnitName.slice(0, 30) + "\u2026";
        } else {
          return this.productionUnitName;
        }
      }
      return "";
    },
    hasActiveEndOfWorkShiftReportAlerts() {
      return this.activeEndOfWorkShiftReportAlerts?.length > 0;
    },
    timezone() {
      if (this.activeFactory && this.activeFactory.timezone) {
        return this.activeFactory.timezone;
      } else {
        const zone = moment.tz.guess(true);
        return zone === null || zone === undefined ? "America/Montreal" : zone;
      }
    },
    isDurationOverThreeDays() {
      return isDurationGreaterThanNDays(this.durationMillis, 3);
    },
    productSkuAndNameText() {
      if (this.timeFrame === PRODUCTION_RUN) {
        if (!this.currentProductionRunCoverage?.sku) {
          return this.$t("dashboard.productionRun.noProductSelected");
        }
        const sku = this.currentProductionRunCoverage.sku;
        const name = this.currentProductionRunCoverage.product_name;
        if (sku && name) {
          return `${sku} (${name})`;
        } else {
          return sku;
        }
      } else {
        if (!this.currentProductSku) {
          return this.$t("dashboard.productionRun.noProductSelected");
        }
        const sku = this.currentProductSku;
        const name = DemoService.maskProductNameIfNecessary(this.email, this.currentProductName);
        if (sku && name) {
          return `${sku} (${name})`;
        }
        return sku;
      }
    },
    workOrderAndLotInformationText() {
      if (this.timeFrame === PRODUCTION_RUN) {
        const workOrderLabel = this.currentProductionRunCoverage?.work_order_label;
        const lotLabel = this.currentProductionRunCoverage?.lot_label;
        if (!workOrderLabel && !lotLabel) return "";
        let separator = workOrderLabel && lotLabel ? " / " : "";
        let workOrderText = workOrderLabel ? this.$t("dashboard.panelHeader.workOrder", [workOrderLabel]) : "";
        let lotNumberText = lotLabel ? this.$t("dashboard.panelHeader.lotNumber", [lotLabel]) : "";
        return workOrderText + separator + lotNumberText;
      } else {
        if (!this.currentWorkOrder && !this.currentLot) {
          return "";
        }
        let separator = this.currentWorkOrder && this.currentLot ? " / " : "";
        let workOrderText = this.currentWorkOrder
          ? this.$t("dashboard.panelHeader.workOrder", [this.currentWorkOrder])
          : "";
        let lotNumberText = this.currentLot ? this.$t("dashboard.panelHeader.lotNumber", [this.currentLot]) : "";
        return workOrderText + separator + lotNumberText;
      }
    },
    showDashboardRange() {
      return !this.isLiveData && (this.timeFrame === "production_run" || this.timeFrame === "shift");
    },
    samplingTypeDefault() {
      return (
        this.preferences?.dashboards?.production_units?.find(
          (puDashboard) => puDashboard.id === this.activeProductionUnitId,
        )?.sampling ?? "assisted"
      );
    },
    samplingButtonLabel() {
      switch (this.samplingTypeDefault) {
        case "manual":
          return this.$t("dashboard.productSampling.manualSampling");
        case "assisted":
          return this.$t("dashboard.productSampling.assistedSampling");
        default:
          return this.$t("dashboard.productSampling.assistedSampling");
      }
    },
    activeEndOfWorkShiftReportAlerts() {
      if (!this.activeAlertInstances || !this.alerts) {
        return [];
      }
      // eslint-disable-next-line no-unused-vars
      const flattenedAlertInstances = Array.from(this.activeAlertInstances, ([key, items]) => items)
        ?.flat()
        ?.filter((instance) => instance.productionUnitId === this.activeProductionUnitId);

      const filteredAlerts = this.alerts.filter((alert) => alert.type === "EndOfShiftReport");
      return (
        filteredAlerts.filter((alert) => flattenedAlertInstances.find((instance) => instance.alertId === alert.id)) ??
        []
      );
    },
    isShiftReportLoading() {
      return (
        this.shiftReportLoadingState === SHIFT_REPORT_LOADING_STATE_AWAITING_PARAMS ||
        this.shiftReportLoadingState === SHIFT_REPORT_LOADING_STATE_IN_PROGRESS ||
        this.shiftReportLoadingState === SHIFT_REPORT_LOADING_STATE_FETCHING
      );
    },
  },
  watch: {
    isAccessible() {
      this.version++;
    },
    activeAlertInstance(newValue, oldValue) {
      if (newValue && !oldValue) {
        if (!this.isShiftReportsDialogOpen) {
          const instance = newValue.instance;
          const found = this.activeEndOfWorkShiftReportAlerts.find(
            (activeWsAlerts) => activeWsAlerts.id === instance.alertId,
          );
          if (found) {
            this.openShiftReportDialog();
          }
        }
      }
    },
    activeEndOfWorkShiftReportAlerts(newValue, oldValue) {
      if (newValue && newValue.length > 0 && newValue.length !== oldValue.length) {
        const { added } = this.diffAlertInstanceIds(newValue, oldValue);
        if (added) {
          this.checkIfReportNeedsToBeOpen();
        }
      }
    },
    oldestDataSourceAlert() {
      this.isDataSourceAlerts = this.oldestDataSourceAlert !== null && this.oldestDataSourceAlert !== undefined;
    },
    activeProductionUnit(newPu, oldPu) {
      if (newPu && newPu?.id !== oldPu?.id) {
        if (this.isShiftReportsDialogOpen) {
          this.setIsShiftReportsDialogOpen(false);
        }
        this.reloadShiftReports();
      }
    },
    isShiftReportsDialogOpen(newValue, oldValue) {
      if (newValue !== oldValue && !newValue) {
        this.tryReloadLatestShiftReports();
      }
    },
    productionRunCoverage(newValue, oldValue) {
      if (newValue?.length !== oldValue?.length) {
        this.reloadShiftReports();
      }
    },
    users() {
      this.reloadShiftReports();
    },
    giveawayViewToggle() {
      if (!this.giveawayViewToggle) this.setGiveawayForSku(null);
      this.startFetchDashboard();

      const tab = this.giveawayViewToggle ? "giveaway" : "timeline";
      this.updateProductionUnitDashboardTab(tab);
    },
    selectedDowntime(current, previous) {
      // slowly scroll down to JustificationPanel when clicking a downtime
      if (!current || !this.$vuetify.breakpoint.mobile || (current && previous && current.id === previous.id)) return;
      goTo(document.body.scrollHeight, {
        duration: 1500,
      });
    },
    isTimeRangeReady() {
      this.restartFetch();
    },
    samplingButton() {
      if (this.samplingButton.length === 0) return;
      // this is just to always have both button unselected so that they visually look the same
      this.$nextTick(() => {
        this.samplingButton = [];
      });
    },
  },
  methods: {
    ...mapActions("navigation", ["fetchFactoryDataSourceAlerts", "setActiveFactory"]),
    ...mapActions("shiftReports", ["reloadLatestShiftReports"]),
    ...mapMutations("shiftReports", ["setIsShiftReportsDialogOpen"]),
    ...mapActions("dashboard", [
      "fetchDashboard",
      "setCurrentDowntimeDuration",
      "setSamplingSessionInProgress",
      "setGiveawayViewToggle",
      "fetchProductionUnitCoverage",
      "fetchProductionUnitProducts",
      "setGiveawayForSku",
      "fetchProductionUnitTags",
      "clearCurrentGiveawaySku",
      "setActiveProductionUnitId",
    ]),
    ...mapActions("operation", ["handleHttpError"]),
    ...mapActions("user", ["updateProductionUnitDashboardTab"]),
    checkIfReportNeedsToBeOpen() {
      if (
        !this.isShiftReportsDialogOpen &&
        this.activeEndOfWorkShiftReportAlerts &&
        this.activeEndOfWorkShiftReportAlerts.length > 0
      ) {
        this.setIsShiftReportsDialogOpen(true);
      }
    },
    diffAlertInstanceIds(newValues, oldValues) {
      const oldSafe = oldValues || new Map();

      const extractIds = (map) =>
        Array.from(map.values())
          .flat()
          .map((inst) => inst.id);

      const newIds = extractIds(newValues);
      const oldIds = extractIds(oldSafe);

      const newSet = new Set(newIds);
      const oldSet = new Set(oldIds);

      const sizeChanged = newSet.size !== oldSet.size;
      const added = newIds.filter((id) => !oldSet.has(id));
      const removed = oldIds.filter((id) => !newSet.has(id));

      return {
        changed: sizeChanged || added.length > 0 || removed.length > 0,
        added,
        removed,
      };
    },
    reloadShiftReports() {
      // do not reload while this is open. This is annoying.
      if (!this.isShiftReportsDialogOpen) {
        this.tryReloadLatestShiftReports();
        this.checkIfReportNeedsToBeOpen();
      }
    },
    openShiftReportDialog() {
      this.setIsShiftReportsDialogOpen(true);
    },
    tryReloadLatestShiftReports() {
      if (
        this.activeFactory &&
        this.alerts &&
        this.workShiftCoverage &&
        this.productionRunCoverage &&
        this.activeProductionUnit
      ) {
        this.reloadLatestShiftReports();
      }
    },
    startSampling(samplingType) {
      if (!this.currentProductId || !this.currentProduct) return null;
      if (!this.currentProduct?.target?.unit) {
        this.showInvalidSamplingSessionDlg = true;
        return;
      }
      // 1. Fetch the current sampling session in progress if any
      GiveawaySamplingService.fetchSessionInProgress(this.activeProductionUnitId)
        .then((httpResponse) => {
          // if 200, set the session in the store
          this.setSamplingSessionInProgress(httpResponse.data);
          // check if the session in progress matches the currently selected product
          if (this.currentProductSku === this.samplingSessionInProgress.sku) this.showExistingSessionDialog = true;
          else this.showWrongProductForSessionDialog = true;
        })
        .catch((httpError) => {
          // if 404, create a new sampling session
          if (
            httpError.response.status === 404 &&
            httpError.response.data.errors[0] &&
            (httpError.response.data.errors[0].code === "CS_GS_GET_10002" || // Deprecated
              httpError.response.data.errors[0].code === "DSH_GSS_GET_10002")
          ) {
            // create new sampling session
            this.createNewSamplingSession(samplingType);
          } else this.handleHttpError(httpError);
        });
    },
    createNewSamplingSession(samplingType) {
      GiveawaySamplingService.createSamplingSession(
        this.activeProductionUnitId,
        { sku: this.currentProductSku },
        samplingType,
      )
        .then((httpResponse) => {
          this.setSamplingSessionInProgress(httpResponse.data.resource);
          this.$router.push(RouteService.toProductSampling(this.currentProductId, samplingType));
        })
        .catch(this.handleHttpError);
    },
    onExistingSessionBack() {
      this.setSamplingSessionInProgress(null);
      this.showExistingSessionDialog = false;
      this.showWrongProductForSessionDialog = false;
      this.cancellingSession = false;
    },
    onWrongProductSelectedCancel() {
      this.showWrongProductForSessionDialog = false;
      this.cancellingSession = true;
    },
    onExistingSessionResume() {
      let samplingType = this.samplingSessionInProgress?.capture_mode === "automated" ? "assisted" : "manual";
      this.$router.push(RouteService.toProductSampling(this.currentProductId, samplingType));
    },
    cancelSessionInProgress() {
      GiveawaySamplingService.cancelSamplingSession(
        this.activeProductionUnitId,
        this.samplingSessionInProgress.session_id,
      )
        .then(() => {})
        .catch(this.handleHttpError);
    },
    getTimelineButtonColor() {
      return this.giveawayViewToggle ? null : "primary";
    },
    getGiveawayButtonColor() {
      return this.giveawayViewToggle ? "primary" : null;
    },
    toProduction() {
      this.setGiveawayViewToggle(false);
    },
    toGiveaway() {
      this.setGiveawayViewToggle(true);
    },
    calculateMinDate() {
      return DateTime.now()
        .setZone(this.timezone)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .minus({ months: this.maxPastMonths })
        .toISO(); // Allow to navigate up to `this.maxPastMonths` month behind !
    },
    /**
     * INTERVALS
     */
    startFetchDashboard() {
      clearInterval(this.fetchDashboardInterval);
      this.fetchDashboard();
      this.fetchDashboardInterval = setInterval(() => this.fetchDashboard(), INTERVAL_10_SECONDS);
    },
    startCurrentDowntimeDuration() {
      clearInterval(this.setCurrentDowntimeDurationInterval);
      this.setCurrentDowntimeDuration();
      this.setCurrentDowntimeDurationInterval = setInterval(() => this.setCurrentDowntimeDuration(), INTERVAL_1_SECOND);
    },
    startFetchFactoryDataSourceAlerts() {
      clearInterval(this.fetchDataSourceAlertsInterval);
      this.fetchDataSourceAlertsInterval = setInterval(() => this.fetchFactoryDataSourceAlerts(), INTERVAL_1_MINUTE);
    },
    startFetchProductionUnitTags() {
      clearInterval(this.fetchProductionUnitTagsInterval);
      this.fetchProductionUnitTags();
      this.fetchProductionUnitTagsInterval = setInterval(this.fetchProductionUnitTags, INTERVAL_10_SECONDS);
    },
    restartFetch() {
      this.clearCurrentGiveawaySku();
      this.startFetchDashboard();
      this.startCurrentDowntimeDuration();
      this.fetchProductionUnitProducts();
      this.startFetchFactoryDataSourceAlerts();
      this.startFetchProductionUnitTags();
    },
    clearIntervals() {
      clearInterval(this.fetchDashboardInterval);
      clearInterval(this.fetchDataSourceAlertsInterval);
      clearInterval(this.setCurrentDowntimeDurationInterval);
      clearInterval(this.fetchProductionUnitTagsInterval);
    },
    displayProductionRunOrShiftStartDate() {
      return this.displayProductionRunOrShiftDate("start_date");
    },
    displayProductionRunOrShiftEndDate() {
      return this.displayProductionRunOrShiftDate("end_date");
    },
    displayProductionRunOrShiftDate(dateType) {
      const date = this.getDateByTimeFrame(dateType);
      if (!date) return "";
      return this.formatProductionRunOrShiftDate(date);
    },
    getDateByTimeFrame(dateType) {
      if (this.timeFrame === PRODUCTION_RUN && this.currentProductionRunCoverage) {
        return this.currentProductionRunCoverage[dateType];
      } else if (this.timeFrame === SHIFT && this.currentWorkShiftCoverage) {
        return this.currentWorkShiftCoverage[dateType];
      } else {
        return null;
      }
    },
    formatProductionRunOrShiftDate(date) {
      if (!date) return "";
      const formattedDate = date.slice(0, 19).replace("T", " ");
      if (this.isSameDay(this.getDateByTimeFrame("start_date"), this.getDateByTimeFrame("end_date"))) {
        return date.slice(11, 19);
      } else {
        return formattedDate;
      }
    },
    isSameDay(date1, date2) {
      return date1 && date2 && date1.slice(0, 10) === date2.slice(0, 10);
    },
    hideInvalidSamplingSessionDlg() {
      this.showInvalidSamplingSessionDlg = false;
    },
    navigateToInvalidPrd() {
      this.showInvalidSamplingSessionDlg = false;
      if (!this.currentProductId) {
        console.warn("Can't navigate to product edit, no current product id set");
        return;
      }
      this.$router.push(RouteService.toProductEdition(this.currentProductId));
    },
  },
  mounted() {
    if (!this.activeProductionUnitId) {
      this.setActiveProductionUnitId(this.$route.params.id);
    }
    this.restartFetch();

    this.tilesRowHeight = this.$refs.tilesRow ? this.$refs.tilesRow[0]?.clientHeight + "px" : "0px";
    const isGiveaway = this.selectedTab === "giveaway";
    this.setGiveawayViewToggle(isGiveaway);
    this.checkIfReportNeedsToBeOpen();
  },
  beforeDestroy() {
    this.clearIntervals();
  },
};
</script>

<style lang="scss" scoped>
.hide-panel {
  display: none;
}

.dropdown-nav {
  flex-grow: 0;
  max-width: 20rem;

  @media ($wx-xl-min) {
    max-width: 22rem;
  }
}

/**
 * Manage the height distribution of the 3 main zone of the page
 */

.main-content-wrapper,
.dashboard-content {
  display: flex;
  flex-direction: column;
}

// Manage responsive height
.main-content-wrapper {
  min-height: 500px;

  @media ($wx-isNotMobile) {
    height: 100%;
  }

  .dashboard-content {
    height: 100%;

    .v-item-group {
      flex-grow: 1;
    }
  }
}

.v-application {
  &:not(.presenter) {
    .main-content-wrapper {
      @media ($wx-lg-min) {
        min-height: 600px;
      }
      @media ($wx-xl-min) {
        min-height: 700px;
      }
    }
  }
  /**
   * If presenter TV display mode, prevent vertical scrollbar.
   * Officially we only support viewport ration of 16:9,
   * but up to a squessed ratio of 199:55, everything displays OK.
   */
  &.presenter {
    .main-content-wrapper {
      @media ($wx-lg-min) {
        min-height: 540px;
      }
      @media ($wx-xl-min) {
        min-height: 650px;
      }
    }
  }
}

.v-window {
  display: flex;
  overflow: visible;

  ::v-deep .v-window__container {
    flex-grow: 1;
    margin: 0 calc(#{var(--grid-gutter)} * -1);
  }
  .v-window-item {
    position: relative;
    padding: calc((var(--grid-gutter) / 3) + 12px) var(--grid-gutter) var(--grid-gutter);

    .tab-inner {
      position: relative;
      height: 100%;
    }

    @media ($wx-md-min) {
      height: 100%;
      overflow: hidden;

      .tab-inner {
        min-height: 450px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }
    &:not(.presenter) {
      @media ($wx-md-min) {
        .tab-inner {
          height: calc(100vh - 200px);
        }
      }

      @media ($wx-lg-min) {
        .tab-inner {
          min-height: 600px;
          height: calc(100vh - 210px);
        }
      }

      @media ($wx-xl-min) {
        .tab-inner {
          min-height: 700px;
          height: calc(100vh - 235px);
        }
      }
    }

    &.presenter {
      @media ($wx-md-min) {
        .tab-inner {
          height: calc(100vh - 250px);
        }
      }

      @media ($wx-lg-min) {
        .tab-inner {
          min-height: 540px;
          height: calc(100vh - 270px);
        }
      }

      @media ($wx-xl-min) {
        .tab-inner {
          min-height: 650px;
          height: calc(100vh - 310px);
        }
      }
    }
  }
}

/**
 * Dashbords's 3 rows of contents in each tabs
 * within `section.dashboard-content`
 */
.tile-container,
.panel-container {
  position: relative;
}

// 1st row
.tile-container {
  margin-bottom: var(--grid-gutter);

  @media ($wx-md-min) {
    height: calc(40% - var(--grid-gutter));
    justify-content: flex-start;
  }

  .tile-slider {
    display: flex;
    column-gap: var(--grid-gutter);
    flex-flow: row wrap;
    height: 100%;

    @media (max-width: $wx-max-width-phone-vertical) {
      justify-content: center;
    }
    @media (min-width: $wx-min-width-phone-vertical) {
      justify-content: flex-start;
    }
    @media ($wx-md-min) {
      column-gap: 0;
      flex-flow: row nowrap;
      transform: translateX(calc((100% - var(--grid-gutter) / 2 * 4) / 5 * -1 - (var(--grid-gutter) / 2)));
      transition: var(--smooth-transition);

      &::after {
        content: none;
      }

      // .tile-slider > .tile
      ::v-deep .tile.v-card {
        &:first-child,
        &:nth-child(6) {
          transition: var(--smooth-transition);
        }
        &:first-child {
          transform: translateX(calc(var(--grid-gutter) * -1.5));
        }
      }
      // .tile-slider.isDataSourceAlerts
      &.isDataSourceAlerts {
        transform: translateX(0);

        // .tile-slider.isDataSourceAlerts > .tile
        .tile {
          &:first-child {
            transform: translateX(0);
          }
          &:nth-child(6) {
            transform: translateX(calc(var(--grid-gutter) * 1.5));
          }
        }
      }
    }
    @media ($wx-lg-min) {
      // .tile-slider
      margin-bottom: calc(var(--grid-gutter) * 1.5);
    }
  }
}

.panel-container {
  // 2nd row panels
  &--production-timeline,
  &--giveaway {
    height: auto;
    margin-bottom: var(--grid-gutter);

    @media ($wx-md-min) {
      margin-bottom: 0;
      position: absolute;
      z-index: 1;
      height: 60%;
      width: 100%;
      left: 0;
      bottom: 0;
      transition: var(--big-transition); // big elements requires more time to move to prevent dizziness

      &.isUp {
        transform: translateY(-66.666666%);
      }
    }

    // Both panel-timeline-oee and panel-giveaway
    ::v-deep .wx-panel.v-card {
      transition: var(--smooth-transition);
      height: 100%;

      @at-root .panel-container--production-timeline.isUp #{&} {
        box-shadow: var(--box-shadow-high-elevation);
      }
    }
  }
  // 3rd row panel
  &--justification {
    ::v-deep .panel-downtime-justification {
      height: auto;
    }

    @media ($wx-md-min) {
      height: calc(40% - var(--grid-gutter));
      ::v-deep .panel-downtime-justification {
        height: calc(100% - 1px);
      }
    }
  }
}

// Conditional chip
.main-title {
  .no-data-warning {
    max-height: 1.3em;
    color: black;
    font-weight: 400; // Normal
  }
  ::v-deep .v-input--switch {
    .v-input__slot {
      margin-bottom: 0;
    }
    .v-label {
      font-size: var(--font-size-h3) !important;
    }
  }

  &--toggle-buttons-active {
    // mobilePhone
    @media ($wx-xs-max) {
      .main-title {
        &__text {
          display: block;
          margin-bottom: 0.25rem;
        }
        &__buttons.v-item-group {
          width: 100%;
          .v-btn {
            width: 50%;
          }
        }
      }
    }
    // mobileTablet & desktop
    @media ($wx-sm-min) {
      display: flex;
      align-items: center;

      .main-title {
        &__buttons {
          margin-top: 0.05em;
        }
      }
    }
  }
}

// WxDatePickerByTimeframe
::v-deep .main-title-tab--giveaway .datepicker-col--first-column {
  // Fixing responsive issue in FR on mini tablets
  @media (min-width: $wx-breakpoint-md) and (max-width: $wx-breakpoint-lg) {
    flex-direction: column;
    .timeframe--aside {
      padding-left: 0.6rem;
      // .timeframe--aside__shift-name
      &__shift-name,
      .wx-contextualized-help__activator.text {
        position: absolute;
      }
      // .timeframe--aside__datepicker-activator
      &__datepicker-activator {
        margin-left: 14.25rem !important;
      }
    }
  }
}
::v-deep .datepicker-col--optional-column-container {
  .measures-export-dialog__activator-btn,
  .sampling-btn {
    @media ($wx-isMobile) {
      flex-grow: 1;

      @media ($wx-xs-max) {
        width: 70%;
        min-width: 15rem; // matching FR button text length
      }
    }
    @media ($wx-isNotMobile) {
      flex-grow: 0;
    }
    @media ($wx-lg-min) {
      min-width: 8.5rem;
    }
    @media ($wx-xl-min) {
      min-width: 14rem;
    }
  }
}

::v-deep .v-badge__badge {
  animation: bounce 1s infinite;

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(-25%);
      animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
    }
    50% {
      transform: none;
      animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }
  }
}
</style>
